// components/Exam.tsx
import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChevronLeft,
  ChevronRight,
  ListChecks,
  Clock,
  ArrowLeft,
} from "lucide-react";
import { getAuthToken } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";

interface Question {
  id: string;
  question: string;
  options: string[];
  category: string;
  language?: string;
}

interface ExamProps {
  endpoint: string;
  sessionType: string;
  title: string;
  duration?: number;
  limit: number;
  onExit?: () => void;
  language: string; // ✅ required from parent
}

type QuestionStatus = "notVisited" | "attempted" | "skipped" | "marked";

const apiURL = import.meta.env.VITE_API_URL || "http://localhost:7845";

export function Exam({
  endpoint,
  sessionType,
  title,
  duration = 600,
  limit = 30,
  onExit,
  language,
}: ExamProps) {
  const [examStarted, setExamStarted] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(duration);
  const [questionStatus, setQuestionStatus] = useState<{
    [key: number]: QuestionStatus;
  }>({});

  const { toast } = useToast();

  // Fetch questions
  const { data: questions, isLoading } = useQuery<Question[]>({
    queryKey: [endpoint, language, sessionType],
    queryFn: async () => {
      const res = await fetch(
        `${apiURL}/${endpoint}?language=${language}&limit=${limit}&exam=true`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      if (!res.ok) throw new Error("Failed to fetch questions");
      return res.json();
    },
    enabled: examStarted,
  });

  // Submit exam
  const submitExam = useMutation({
    mutationFn: async (payload: any) => {
      const token = getAuthToken();
      const res = await fetch(`${apiURL}/api/examination/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to submit exam");
      }

      return res.json();
    },
    onSuccess: (data) => {
      setSubmitted(true);
      toast({
        title: "Exam Submitted!",
        description: `Score: ${data.score}, Percentage: ${data.percentage}%`,
      });

      if (onExit) onExit();
    },
    onError: (error: any) => {
      toast({
        title: "Submission Failed",
        description: error?.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

  // Timer
  useEffect(() => {
    if (!examStarted || submitted) return;
    if (timeLeft <= 0) {
      handleSubmit();
      return;
    }
    const timer = setInterval(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [examStarted, timeLeft, submitted]);

  const handleAnswer = (idx: number) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [currentIndex]: idx }));
    setQuestionStatus((prev) => ({ ...prev, [currentIndex]: "attempted" }));
  };

  const handleSkip = () => {
    if (submitted) return;
    setQuestionStatus((prev) => ({ ...prev, [currentIndex]: "skipped" }));
    setCurrentIndex((i) => Math.min(i + 1, total - 1));
  };

  const handleMark = () => {
    if (submitted) return;
    setQuestionStatus((prev) => ({ ...prev, [currentIndex]: "marked" }));
    setCurrentIndex((i) => Math.min(i + 1, total - 1));
  };

  const handleSubmit = () => {
    if (!questions || submitted || submitExam.isPending) return;

    const timeTaken = duration - timeLeft;

    const payload = {
      sessionType,
      language,
      answers: questions.map((q, idx) => {
        const selectedOption = answers[idx] ?? null;
        return {
          questionId: q.id,
          selectedOption,
          attempted: selectedOption !== null,
        };
      }),
      totalQuestions: questions.length,
      totalTime: duration,
      timeTaken,
    };

    submitExam.mutate(payload);
  };

  const handleBack = () => {
    const confirmExit = window.confirm(
      "Are you sure you want to exit the exam? Your progress will be lost!"
    );
    if (confirmExit && onExit) {
      onExit();
    }
  };

  const getTileClass = (index: number) => {
    const status = questionStatus[index];
    switch (status) {
      case "attempted":
        return "bg-green-200 dark:bg-green-700 border-green-500 text-gray-900 dark:text-white";
      case "skipped":
        return "bg-yellow-200 dark:bg-yellow-700 border-yellow-500 text-gray-900 dark:text-white";
      case "marked":
        return "bg-purple-200 dark:bg-purple-700 border-purple-500 text-gray-900 dark:text-white";
      default:
        return "bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white";
    }
  };

  const total = questions?.length || 0;
  const current = questions?.[currentIndex];

  if (!examStarted) return null;
  if (isLoading) return <Skeleton className="h-8 w-64 mx-auto mb-4" />;

  return (
    <div
      className="fixed inset-0 bg-white dark:bg-gray-900 overflow-auto p-4 sm:p-6 z-50"
      style={{ userSelect: "none" }}
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h2 className="text-2xl md:text-3xl font-bold flex items-center">
          <ListChecks className="text-[hsl(201,91%,60%)] mr-2" /> {title}
        </h2>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 text-base sm:text-lg">
            <Clock />
            {String(Math.floor(timeLeft / 3600)).padStart(2, "0")}:
            {String(Math.floor((timeLeft % 3600) / 60)).padStart(2, "0")}:
            {String(timeLeft % 60).padStart(2, "0")}
          </div>
          <Button
            onClick={handleBack}
            variant="destructive"
            className="w-full sm:w-auto"
          >
            <ArrowLeft size={16} className="mr-2" /> Back
          </Button>
        </div>
      </div>

      {/* Exam content */}
      <div className="flex flex-col gap-6">
        {/* Question content */}
        <div className="flex-1">
          {current ? (
            <Card className="gcep-card p-4 sm:p-8 w-full max-w-full">
              <div className="flex flex-col sm:flex-row justify-between mb-4 sm:mb-6 text-sm sm:text-base">
                <span>
                  Question {currentIndex + 1} of {total}
                </span>
                <span>{Object.keys(answers).length} Answered</span>
              </div>

              <h3 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">
                {current.question}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6">
                {current.options.map((opt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(idx)}
                    disabled={submitted}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition ${
                      answers[currentIndex] === idx
                        ? "bg-blue-500 border-blue-600 text-white"
                        : "bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                    }`}
                  >
                    {String.fromCharCode(65 + idx)}) {opt}
                  </button>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4 sm:mt-6">
                <div className="flex gap-2">
                  <Button onClick={handleSkip} variant="secondary">
                    Skip
                  </Button>
                  <Button onClick={handleMark} variant="outline">
                    Mark for Review
                  </Button>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                    disabled={currentIndex === 0}
                    variant="secondary"
                  >
                    <ChevronLeft size={16} className="mr-2" /> Previous
                  </Button>

                  {currentIndex < total - 1 ? (
                    <Button
                      onClick={() => setCurrentIndex((i) => i + 1)}
                      className="gcep-primary-btn"
                    >
                      Next <ChevronRight size={16} className="ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      className="gcep-primary-btn"
                      disabled={submitted || submitExam.isPending}
                    >
                      {submitExam.isPending ? "Submitting..." : "Submit Exam"}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ) : (
            <Card className="p-8 text-center">No questions available</Card>
          )}
        </div>

        {/* Navigator */}
        <div className="border rounded-lg p-3 sm:p-4">
          <h3 className="font-semibold mb-3 sm:mb-4">Question Navigator</h3>
          <div
            className="grid gap-2 sm:gap-3"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(36px, 1fr))",
            }}
          >
            {Array.from({ length: total }).map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded border text-xs sm:text-sm font-medium transition ${getTileClass(
                  idx
                )} ${currentIndex === idx ? "ring-2 ring-blue-500" : ""}`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-2 text-xs sm:text-sm">
            <div className="flex items-center mr-2">
              <span className="inline-block w-4 h-4 bg-green-200 border border-green-500 dark:bg-green-700 mr-2"></span>
              Attempted
            </div>
            <div className="flex items-center mr-2">
              <span className="inline-block w-4 h-4 bg-yellow-200 border border-yellow-500 dark:bg-yellow-700 mr-2"></span>
              Skipped
            </div>
            <div className="flex items-center mr-2">
              <span className="inline-block w-4 h-4 bg-purple-200 border border-purple-500 dark:bg-purple-700 mr-2"></span>
              Marked
            </div>
            <div className="flex items-center mr-2">
              <span className="inline-block w-4 h-4 bg-gray-100 border border-gray-300 dark:bg-gray-700 mr-2"></span>
              Not Visited
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
